import React, { useEffect, useMemo } from 'react';

import { Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useDebounce } from 'hooks/useDebaunce';
import useHashmapConverter from 'hooks/useHashmapConverter';

import { useQueryGetTaskDataByHashCode } from 'api/queries/getTaskDataByHashCode.query';
import { useQueryGetTaskDataByHashCodeSearch } from 'api/queries/getTaskDataByHashCodeSearch.query';

import { Select } from 'components/DesignSystem/Select';

const mapper = (item) => ({
    label: `${item.name} (ID: ${item.activityId.length > 20 ? item.activityId.slice(0, 20) + '...' : item.activityId})`,
    value: String(item.taskVersionHashCode),
});

const MIN_CHARS = 2;

export const SpecificActivitiesSelect = ({ methods, name }) => {
    const hashcode = Object.values(useParams<{ id: string }>()).filter(Boolean).join(',');
    const { versionList: versionFromHashMap } = useHashmapConverter(hashcode);
    const latestVersionId = versionFromHashMap[0]?.versionDbId;

    const { data: initialValue } = useQueryGetTaskDataByHashCode({
        metaDataId: latestVersionId,
        hashCodesList: methods.watch(name),
    });

    const [filter, setFilter] = React.useState('');
    const [value, setValue] = React.useState<ISelectOption<string>[] | null>(null);
    const debouncedValue = useDebounce<string>(filter, 1000);
    const {
        data: tasks,
        isFetching,
        isError,
    } = useQueryGetTaskDataByHashCodeSearch({
        metaDataId: latestVersionId,
        filter: debouncedValue,
    });

    const options = useMemo<ISelectOption<string>[]>(() => (tasks ? tasks.map(mapper) : []), [tasks]);

    const enabled = debouncedValue.length >= MIN_CHARS;

    const handleChange = (value) => {
        setValue(value);
        methods?.setValue(name, value ? value.map((item) => String(item.value)) : null, { shouldDirty: true });
    };

    const noOptionsMessage = () => {
        if (isError) return 'Couldn’t load options';
        if (!enabled && MIN_CHARS > 0) return `Type at least ${MIN_CHARS} characters`;
        return 'No options';
    };

    useEffect(() => {
        if (initialValue) {
            setValue(initialValue.map(mapper));
        }
    }, [initialValue]);

    return (
        <Controller
            render={({ field }) => (
                <Select<ISelectOption<string>, true>
                    {...field}
                    options={options}
                    isClearable={true}
                    isLoading={isFetching}
                    onChange={handleChange}
                    value={value}
                    isMulti={true}
                    isOptionDisabled={() => field.value?.length >= 10}
                    placeholder={'Select activity or start typing its name...'}
                    onInputChange={(val, { action }) => {
                        if (action === 'input-change') setFilter(val);
                        return val;
                    }}
                    noOptionsMessage={noOptionsMessage}
                    loadingMessage={() => 'Loading…'}
                    formatGroupLabel={(group) => <div>{group.label}</div>}
                    filterOption={() => true} // important: we already filtered on the server
                />
            )}
            name={name}
            control={methods?.control}
        />
    );
};
